/**
 * Convierte lo que el usuario eligió —fotos, capturas, un PDF— en páginas JPEG
 * listas para subir.
 *
 * Resuelve dos problemas que el servidor no puede resolver solo:
 *
 * **HEIC.** Es el formato por default de la cámara del iPhone y el pipeline del
 * backend lo rechaza (no hay decodificador HEIC en ImageSharp, y meter uno nativo
 * al contenedor es una dependencia que no se paga sola). Pero el navegador de iOS
 * **sí** sabe decodificarlo: `createImageBitmap` usa el códec del sistema. Pasar
 * toda imagen por un canvas antes de subirla convierte el HEIC en JPEG del lado
 * de quien tiene el códec. De paso baja una foto de 4 MB a unos cientos de KB,
 * que en datos móviles dentro de un restaurante no es un detalle.
 *
 * **PDF.** El backend sólo acepta imágenes, y eso se mantiene a propósito. Aquí
 * se rasteriza cada página con pdf.js —el navegador ya es un renderizador de
 * PDF— en vez de meter PDFium nativo en el contenedor de Render. Funciona igual
 * con un PDF de diseño (texto vectorial) que con uno escaneado, y el servidor
 * recibe fotos vengan de donde vengan.
 *
 * **Si algo de esto falla, el archivo se sube tal cual.** El backend tiene sus
 * propios mensajes para HEIC y para PDF: degradar a "el servidor te dice qué
 * hacer" siempre es mejor que tragarse el archivo aquí.
 */

/** Espejo de MenuIngestLimits.PageMaxDimension: la letra chica de una carta lo necesita. */
const MAX_DIMENSION = 2200;

/** Espejo de MenuIngestLimits.PageJpegQuality. */
const JPEG_QUALITY = 0.85;

/** Espejo de MenuIngestLimits.MaxPagesPerIngestion. */
export const MAX_PAGES = 10;

export interface NormalizedPage {
  blob: Blob;
  fileName: string;
}

export interface NormalizeResult {
  pages: NormalizedPage[];
  /** Avisos para el usuario. Nunca errores: si algo falló, la página va igual. */
  warnings: string[];
}

const isPdf = (file: File) =>
  file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

/** Cambia la extensión al formato real. Un nombre que miente rompe la descarga en Windows. */
const asJpgName = (name: string, suffix = '') => {
  const base = name.replace(/\.[^.]+$/, '') || 'pagina';
  return `${base}${suffix}.jpg`;
};

/**
 * Dibuja un bitmap ya decodificado en un canvas del tamaño correcto y lo saca
 * como JPEG.
 */
const bitmapToJpeg = async (bitmap: ImageBitmap, fileName: string): Promise<NormalizedPage> => {
  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('El navegador no permitió usar canvas');

  // Fondo blanco antes de dibujar: un PNG con transparencia se convierte en JPEG
  // con fondo negro, y una carta sobre negro es ilegible para el modelo.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
  );

  if (!blob) throw new Error('No se pudo convertir la imagen');

  return { blob, fileName: asJpgName(fileName) };
};

/**
 * Rasteriza un PDF, una imagen por página.
 *
 * pdf.js se importa en lazy y sólo cuando hace falta: son varios cientos de KB
 * que la mayoría de los usuarios —los que suben fotos— no tienen por qué pagar.
 */
const rasterizePdf = async (file: File, budget: number): Promise<NormalizeResult> => {
  const pdfjs = await import('pdfjs-dist');

  // El worker se empaqueta con el bundle, no se trae de un CDN: esto corre en
  // restaurantes con el wifi filtrado, y un worker que no carga dejaría el PDF
  // sin convertir sin decir por qué.
  //
  // `pdf.worker.entry` es el punto de entrada que webpack sabe empaquetar solo;
  // no trae tipos porque su único propósito es ser un módulo que registra el
  // worker y devuelve su URL.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore -- el entry del worker no publica tipos
  const workerEntry = await import('pdfjs-dist/build/pdf.worker.entry');
  pdfjs.GlobalWorkerOptions.workerSrc = workerEntry.default ?? workerEntry;

  const data = new Uint8Array(await file.arrayBuffer());
  // isEvalSupported: false cierra CVE-2024-4367 (un PDF con una fuente
  // manipulada puede ejecutar JS arbitrario) sin tener que subir a pdfjs 4.x.
  const doc = await pdfjs.getDocument({ data, isEvalSupported: false }).promise;

  const pages: NormalizedPage[] = [];
  const warnings: string[] = [];

  const pageCount = Math.min(doc.numPages, budget);
  if (doc.numPages > budget) {
    warnings.push(
      `El PDF tiene ${doc.numPages} páginas y sólo se pueden cargar ${budget} a la vez. ` +
        `Se tomaron las primeras ${budget}.`
    );
  }

  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
    const page = await doc.getPage(pageNumber);

    // La escala se calcula contra el tamaño real de la página para llegar al lado
    // mayor que queremos: renderizar a escala 1 deja la letra chica de una carta
    // en el terreno donde el modelo adivina dígitos.
    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(3, MAX_DIMENSION / Math.max(base.width, base.height));
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('El navegador no permitió usar canvas');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: ctx, viewport }).promise;

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
    );

    if (blob) {
      pages.push({ blob, fileName: asJpgName(file.name, `-p${pageNumber}`) });
    }
  }

  return { pages, warnings };
};

/**
 * Normaliza los archivos elegidos. Devuelve las páginas en el orden en que el
 * usuario las eligió, acotadas a `budget`.
 */
export const normalizeMenuFiles = async (
  files: File[],
  budget: number = MAX_PAGES
): Promise<NormalizeResult> => {
  const pages: NormalizedPage[] = [];
  const warnings: string[] = [];

  for (const file of files) {
    if (pages.length >= budget) {
      warnings.push(
        `Sólo se pueden cargar ${budget} páginas a la vez; el resto de los archivos quedó fuera.`
      );
      break;
    }

    const remaining = budget - pages.length;

    if (isPdf(file)) {
      try {
        const result = await rasterizePdf(file, remaining);
        pages.push(...result.pages);
        warnings.push(...result.warnings);

        if (result.pages.length === 0) {
          warnings.push(`No se pudo convertir "${file.name}" a imágenes.`);
        }
      } catch {
        // Se sube el PDF crudo: el backend responde con su mensaje, que dice
        // exactamente qué hacer. Tragarse el archivo aquí dejaría al usuario sin
        // pista de por qué no pasó nada.
        pages.push({ blob: file, fileName: file.name });
      }
      continue;
    }

    try {
      // Aquí es donde el HEIC de iPhone se vuelve JPEG, usando el códec del
      // sistema. En un navegador que no sepa decodificarlo, esto lanza y el
      // archivo sube crudo para que el backend dé su mensaje.
      const bitmap = await createImageBitmap(file);
      try {
        pages.push(await bitmapToJpeg(bitmap, file.name));
      } finally {
        bitmap.close();
      }
    } catch {
      pages.push({ blob: file, fileName: file.name });
    }
  }

  return { pages, warnings };
};
